import React, {
  useCallback, useEffect, useMemo, useState, createRef,
} from 'react';
import * as Styles from './HomeHandler.styles';
import {
  Advertisement, GreetingBanner, Highlight, ContractedProducts, AiConversationBanner,
} from './components';
import { useHistory, useLocation } from 'react-router';
import { useSelector, useDispatch } from 'react-redux';
import { homeActions } from '@/redux/modules/home';
import { LINK_BEHAVIOR, MODULES, PRODUCTS_HIGHLIGHT_IDS } from './HomeHandler.enum';
import {
  preChatActions, sitesActions, studioActions,
} from '@/redux/modules';
import { Resources } from '@/pages/Home/components/Resources';
import { LoadingError } from './components/LoadingError';
import withErrorBoundary from '@/utils/errorBoundary';
import { homeAnalytics } from '@/analytics/home';
import ModalFeaturePro from '../Studio/components/ModalFeaturePro';
import { ID_PRO_FEATURES } from '../Studio/studio.enum';
import { useFeatureValue, useFeatureIsOn } from '@growthbook/growthbook-react';
import {
  AI_AGENTS_HOME_PAGE_BANNER,
  ATIVACAO_N8N_INHOUSE,
  PORTFOLIO_VPS_N8N_FEATURE,
  ENABLE_STUDIO,
  AI_AGENTS_EMAIL_ENABLE_FRONT,
  FOXTROT_HOME_AI_CONVERSATION_BANNER,
  PORTFOLIO_AGENT_FACTORY_NEW_ONBOARDING,
  AI_AGENTS_SITE_BUILDER_AGENT_FACTORY,
  HOME_HIGHLIGHT_VPS_OPEN_CLAW,
} from '@/config/GrowthBook/constants';
import { HgProducts } from '@/components/SupportForms/SupportForms.types';
import { Banner as BannerAI } from '@/components/BannerAI';
import { Popover } from '@/components/Popover';
import { PopoverContent } from '@/components/Popover/PopoverContent';
import useLocale from '@/hooks/useLocale';
import { useStudio } from '@/hooks/studio';
import { useAiChat } from '@/hooks/AiChat';
import { N8N_URL } from '@/config/urls/n8nUrls';
import { SitesActionTypes } from '@/redux/modules/sites/sites.types';
import useAgentsFactory from '@/hooks/agentsFactory/useAgentsFactory';
import { isBRServer } from '@/utils/Validators/validation';

const HomeHandler = () => {
  const [displayStudioTrialModal, setDisplayStudioTrialModal] = useState(false);

  const history = useHistory();
  const location = useLocation();
  const showSitesAiAgentBanner = useFeatureIsOn(AI_AGENTS_HOME_PAGE_BANNER);
  const showAiConversationBanner = useFeatureIsOn(FOXTROT_HOME_AI_CONVERSATION_BANNER);
  const n8nInhouse = useFeatureValue(ATIVACAO_N8N_INHOUSE);
  const bannerGuideRef = createRef(null);
  const { provisioningFreePlan, provisioningTrailWithoutCreditCard } = useStudio();

  const enableVpsN8n = useFeatureIsOn(PORTFOLIO_VPS_N8N_FEATURE);
  const enabledAiAgentsMailChat = useFeatureIsOn(AI_AGENTS_EMAIL_ENABLE_FRONT);
  const enableStudio = useFeatureIsOn(ENABLE_STUDIO);
  const enableAgentFactoryNewOnboarding = useFeatureIsOn(PORTFOLIO_AGENT_FACTORY_NEW_ONBOARDING);
  const enabledAiAgents = useFeatureIsOn(AI_AGENTS_SITE_BUILDER_AGENT_FACTORY);
  const enabledHighlightVpsOpenClaw = useFeatureIsOn(HOME_HIGHLIGHT_VPS_OPEN_CLAW);

  const { handleInitialInteraction } = useAiChat();
  const { openAgentsFactory } = useAgentsFactory();

  const { routes: routesLocale, home: homeLocale, aichat: aichatLocale } = useLocale();

  const { product: studioProduct, loading: studioAccountLoading } = useSelector(state => state.studio);
  const { isIframeOpen } = useSelector(state => state.merlin);
  const { features } = useSelector(state => state.summary);

  const studioPlanId = studioProduct && studioProduct.pid;
  const redirectedFromSites = location.state && location.state.redirectForced === 'sites';
  const focusOn = location.state && location.state.focusOn;

  const {
    homeContent, loading: homeLoading, showLoadingError, sawHome, menuIsOpen,
  } = useSelector(state => state.home);

  const { productsList } = useSelector(state => state.preChat);
  const { product: studioAccountProduct, products: studioProducts, provisionFree } = useSelector(state => state.studio);
  const sitesReducer = useSelector(state => state.sites);

  // Agents Factory eligibility data from Redux
  const agentsFactoryData = useMemo(() => (
    sitesReducer && sitesReducer.agentsFactory
      ? sitesReducer.agentsFactory.account
      : null
  ), [sitesReducer]);

  const isAgentAvailable = useMemo(() => (
    !!(enableAgentFactoryNewOnboarding && agentsFactoryData && agentsFactoryData.eligible && !agentsFactoryData.has_agent)
  ), [agentsFactoryData, enableAgentFactoryNewOnboarding]);

  const activeDomains = useMemo(() => (
    (productsList.domains && productsList.domains.filter(
      product => product && product.status && product.status.toLowerCase() === 'active',
    )) || []
  ), [productsList.domains]);

  const activeHostings = useMemo(() => (
    (productsList.hostings && productsList.hostings.filter(
      product => product && product.status && product.status.toLowerCase() === 'active',
    )) || []
  ), [productsList.hostings]);

  const activeStudio = useMemo(() => (
    (productsList.hostings && productsList.hostings.find(
      product => product && product.status && product.package_id
        && product.status.toLowerCase() === 'active' && HgProducts.STUDIO_ALL.includes(product.package_id),
    ))
  ), [productsList.hostings]);

  const dispatch = useDispatch();

  const clearFocusOn = useCallback(() => {
    if (focusOn) {
      history.replace({
        ...location,
        state: {
          ...location.state,
          focusOn: null,
        },
      });
    }
  }, [history, location, focusOn]);

  const clearRouterState = useCallback(() => {
    history.replace({ ...location, state: null });
  }, [history, location]);

  const setHomeAsSeen = useCallback(() => {
    if (!sawHome && redirectedFromSites) {
      dispatch(homeActions.sawHome());
      clearRouterState();
    }
  }, [dispatch, sawHome, clearRouterState, redirectedFromSites]);

  useEffect(() => {
    setHomeAsSeen();
  }, [setHomeAsSeen]);

  const getHomeRequest = useCallback(() => {
    if (homeContent && homeContent.length) return;

    dispatch(studioActions.products.request());
    dispatch(sitesActions.sites.request());
    dispatch(homeActions.getHomeContent.request());
    dispatch(preChatActions.productByStatus.request({ withCancelled: true, withPending: true }));
    dispatch(studioActions.account.request());
  }, [dispatch, homeContent]);

  useEffect(() => {
    getHomeRequest();
  }, [getHomeRequest]);

  // Trigger Agents Factory eligibility check on mount
  useEffect(() => {
    if (enableAgentFactoryNewOnboarding && features && features.agent_factory) {
      dispatch({ type: SitesActionTypes.AGENTS_FACTORY_CHECK_AVAILABILITY_REQUEST });
    }
  }, [dispatch, enableAgentFactoryNewOnboarding, features]);

  const openAiChat = useCallback(() => {
    if (n8nInhouse && n8nInhouse.home) {
      handleInitialInteraction();
    }
  }, [handleInitialInteraction, n8nInhouse]);

  const handleFunction = useCallback((link) => {
    switch (link) {
      case 'open_merlin':
        openAiChat();
        break;
      case 'open_merlin_sites':
        openAiChat();
        break;
      default:
        // eslint-disable-next-line no-console
        console.log(`function not found: ${link}`);
    }
  }, [openAiChat]);

  const handleElementLink = useCallback((link, behavior) => {
    if (LINK_BEHAVIOR.FUNCTION === behavior) {
      handleFunction(link);
      return;
    }
    if (link && link.includes('http')) {
      switch (behavior) {
        case LINK_BEHAVIOR.NEW_TAB:
          return window.open(link, '_blank');
        case LINK_BEHAVIOR.NEW_WINDOW:
          return window.open(link, '_blank', 'popup');
        case LINK_BEHAVIOR.SAME_TAB:
        default:
          return window.open(link, '_self');
      }
    } else {
      history.push(link, { redirectFromHome: true });
    }
  }, [history, handleFunction]);

  // Helper to append query params when user starts the site creation flow
  const appendFlowParams = useCallback((link) => {
    if (!link) return link;

    const paramKey = 'isAgentAvailable';
    const paramValue = String(isAgentAvailable);

    const appendParamsString = (raw) => {
      const joiner = raw.includes('?') ? '&' : '?';
      return `${raw}${joiner}${paramKey}=${paramValue}`;
    };

    // Absolute URLs
    if (link.startsWith('http')) {
      try {
        const url = new URL(link);
        url.searchParams.set(paramKey, paramValue);
        return url.toString();
      } catch (_) {
        // Fallback in case URL parsing fails
        return appendParamsString(link);
      }
    }

    // Relative paths
    return appendParamsString(link);
  }, [isAgentAvailable]);

  const advertisementProps = useCallback(() => {
    const advertisementModule = homeContent.find(item => item.name === MODULES.ADVERTISEMENTS);
    const advertisementContent = advertisementModule && advertisementModule.elements && advertisementModule.elements.length && advertisementModule.elements[0];

    return ({
      size: advertisementContent && advertisementModule.size,
      title: advertisementContent && advertisementContent.title,
      description: advertisementContent && advertisementContent.description,
      image: advertisementContent && advertisementContent.image,
      cta: {
        label: advertisementContent && advertisementContent.cta,
        onClick: () => {
          homeAnalytics.advertisementsClick();
          advertisementContent && handleElementLink(advertisementContent.link, advertisementContent.link_open_behavior);
        },
      },
      loading: homeLoading,
      shouldRender: activeHostings && activeHostings.length > 0,
    });
  }, [homeContent, handleElementLink, homeLoading, activeHostings]);

  const highLightProps = useCallback(() => {
    const highLightModule = homeContent.filter(element => element.name === MODULES.HIGHLIGHT);

    const getOnClickFunction = (element) => {
      let onClickAction = () => handleElementLink(element.link, element.link_open_behavior);

      const studioFreeAction = () => {
        const alreadyHasFreePlan = activeStudio && activeStudio.package_id === HgProducts.STUDIO_FREE;

        if (alreadyHasFreePlan) {
          handleElementLink(element.link, element.link_open_behavior);
        } else {
          dispatch(studioActions.provisionFree.request({
            onSuccessCallback: () => {
              handleElementLink(element.link, element.link_open_behavior);
              dispatch(homeActions.getHomeContent.reset());
            },
          }));
        }
      };

      if (element.id === PRODUCTS_HIGHLIGHT_IDS.HG_STUDIO_PRO_TRIAL) {
        onClickAction = () => setDisplayStudioTrialModal(true);
      }

      if (element.id === PRODUCTS_HIGHLIGHT_IDS.HG_STUDIO_FREE) {
        onClickAction = () => studioFreeAction();
      }

      if (element.id === PRODUCTS_HIGHLIGHT_IDS.VPS_N8N) {
        onClickAction = () => {
          const hasVpsN8n = activeHostings && activeHostings.some(
            product => HgProducts.VPS_N8N_ALL.includes(product.package_id),
          );
          if (hasVpsN8n) {
            history.push(routesLocale.products);
          } else {
            window.open(N8N_URL, '_blank');
          }
        };
      }

      if (element.id === PRODUCTS_HIGHLIGHT_IDS.HG_BUILDER) {
        onClickAction = () => {
          const linkWithParams = appendFlowParams(element.link);
          handleElementLink(linkWithParams, element.link_open_behavior);
        };
      }

      if (element.id === PRODUCTS_HIGHLIGHT_IDS.AI_AGENTS) {
        onClickAction = () => {
          openAgentsFactory('create');
        };
      }

      return () => {
        homeAnalytics.handleHighlightClick(element.id, studioPlanId);
        onClickAction();
      };
    };

    const staticHighlights = homeLocale.infosMenuHighlight.staticHighlights || [];
    const hasStudio = productsList.hostings && !!productsList.hostings.length
      && productsList.hostings.some(product => product && product.status && product.status.toLowerCase() === 'active' && HgProducts.STUDIO_ALL.includes(product.package_id));

    const highlightsToBeExcluded = enableStudio && hasStudio ? [] : [
      PRODUCTS_HIGHLIGHT_IDS.HG_STUDIO_FREE,
      PRODUCTS_HIGHLIGHT_IDS.HG_STUDIO_PRO,
      PRODUCTS_HIGHLIGHT_IDS.HG_STUDIO_PRO_TRIAL,
    ];

    if (!enableVpsN8n) {
      highlightsToBeExcluded.push(PRODUCTS_HIGHLIGHT_IDS.VPS_N8N);
    }

    if (!enabledAiAgentsMailChat) {
      highlightsToBeExcluded.push(PRODUCTS_HIGHLIGHT_IDS.AI_AGENTS_MAIL_CHAT);
    }

    if (!enabledAiAgents || !features.agent_factory) {
      highlightsToBeExcluded.push(PRODUCTS_HIGHLIGHT_IDS.AI_AGENTS);
    }

    if (isBRServer || enabledHighlightVpsOpenClaw) {
      highlightsToBeExcluded.push(PRODUCTS_HIGHLIGHT_IDS.VPS_N8N);
    }

    if (!isBRServer && !enabledHighlightVpsOpenClaw) {
      highlightsToBeExcluded.push(PRODUCTS_HIGHLIGHT_IDS.VPS_OPEN_CLAW);
    }

    const elementsHighlightApiIds = highLightModule && highLightModule.length ? highLightModule[0].elements
      .filter(element => !highlightsToBeExcluded.includes(element.id))
      .map(element => element.id) : [];

    const staticHighlightsFormatted = staticHighlights
      .filter(element => !highlightsToBeExcluded.includes(element.id))
      .map(element => ({
        ...element,
        cta: {
          label: element.cta,
          onClick: getOnClickFunction(element),
        },
      }));

    const elementsHighlight = staticHighlightsFormatted.filter(
      element => elementsHighlightApiIds.includes(element.id) || element.forceShow,
    );

    const buttonLoadingRules = {
      [PRODUCTS_HIGHLIGHT_IDS.HG_STUDIO_PRO]: false,
      [PRODUCTS_HIGHLIGHT_IDS.HG_STUDIO_PRO_TRIAL]: false,
      [PRODUCTS_HIGHLIGHT_IDS.HG_STUDIO_FREE]: provisionFree.loading,
    };

    const mountButtonIsLoading = elementId => buttonLoadingRules[elementId];

    return ({
      elementsHighlight: elementsHighlight || [],
      focusOn,
      clearFocusOn,
      loading: homeLoading,
      mountButtonIsLoading,
      menuIsOpen,
    });
  }, [
    dispatch,
    handleElementLink,
    appendFlowParams,
    homeLoading,
    focusOn,
    clearFocusOn,
    activeStudio,
    studioPlanId,
    provisionFree.loading,
    setDisplayStudioTrialModal,
    homeLocale.infosMenuHighlight.staticHighlights,
    history,
    routesLocale,
    activeHostings,
    enableVpsN8n,
    enableStudio,
    enabledAiAgentsMailChat,
    enabledAiAgents,
    features,
    homeContent,
    menuIsOpen,
    productsList.hostings,
    openAgentsFactory,
    enabledHighlightVpsOpenClaw,
  ]);

  const hiredProductsProps = useCallback(() => {
    const hiredProductsContent = homeContent.find(item => item.name === MODULES.HIRED_PRODUCTS);
    const advertisementModule = homeContent.find(item => item.name === MODULES.ADVERTISEMENTS);
    const advertisementContent = advertisementModule.elements.length;
    const shouldRenderAdvertisement = activeHostings && activeHostings.length > 0;

    const sizeRules = (advertisementContent > 0) && (shouldRenderAdvertisement);
    const moduleSize = sizeRules ? hiredProductsContent.size : '100';

    return ({
      size: hiredProductsContent && moduleSize,
    });
  }, [homeContent, activeHostings]);

  const bannerProps = useCallback(() => {
    const displayCreateSiteBanner = sitesReducer && sitesReducer.list.find((site) => {
      const isPending = site.status_plan === 'pending' || site.status_domain === 'pending';
      const isInAnalysis = site.status_plan === 'in_analysis' || site.status_domain === 'in_analysis';
      return !isPending && !isInAnalysis;
    });

    return ({
      bannerLocale: homeLocale.greetingBanner,
      event: homeAnalytics.greetingBannerClick,
      loading: homeLoading,
      displayCreateSiteBanner,
    });
  }, [homeLocale.greetingBanner, homeLoading, sitesReducer]);

  const bannerAiProps = useCallback(() => {
    const bannerModule = homeContent.find(item => item.name === MODULES.BANNER);
    const bannerContent = bannerModule && bannerModule.elements && bannerModule.elements.length && bannerModule.elements[0];

    return ({
      description: bannerContent && bannerContent.description,
      primaryButtonLabel: bannerContent && bannerContent.cta,
      primaryButtonAction: () => {
        if (bannerContent) {
          const linkWithParams = appendFlowParams(bannerContent.link);
          handleElementLink(linkWithParams, bannerContent.link_open_behavior);
        }
      },
      primaryButtonRef: bannerGuideRef,
    });
  }, [homeContent, bannerGuideRef, handleElementLink, appendFlowParams]);

  const getPopoverProps = useCallback(() => ({
    title: homeLocale.bannerAiAgent.popover.title,
    description: homeLocale.bannerAiAgent.popover.description,
    buttonLabel: homeLocale.bannerAiAgent.popover.buttonLabel,
  }), [homeLocale.bannerAiAgent.popover]);

  const popoverContent = <PopoverContent {...getPopoverProps()} />;

  const highlightSteps = [
    {
      popover: {
        key: 'HomeBannerButton',
        content: popoverContent,
        pointer: 'topRight',
        $maxWidth: 240,
      },
      mask: {
        customRadius: 100,
      },
    },
  ];

  const resourcesProps = useCallback(() => {
    const resourcesModule = homeContent.find(element => element.name === MODULES.RESOURCES);

    const resourcesContent = resourcesModule && resourcesModule.elements && resourcesModule.elements.length && resourcesModule.elements;

    const elementsResources = resourcesContent && resourcesContent.length && resourcesContent.map(element => ({
      ...element,
      cta: {
        onClick: () => {
          homeAnalytics.handleResourcesClick(element.id);
          handleElementLink(element.link, element.link_open_behavior);
        },
      },
    }));

    return ({
      elementsResources: elementsResources || [],
      loading: homeLoading,
    });
  }, [homeContent, handleElementLink, homeLoading]);

  const popoverStorageKey = 'notShowHomeBannerPopover';
  const notShowBannerPopover = localStorage.getItem(popoverStorageKey) || isIframeOpen;

  const Banner = () => (showSitesAiAgentBanner ? <BannerAI {...bannerAiProps()} /> : <GreetingBanner {...bannerProps()} />);

  const renderModule = (module) => {
    if (!module.elements || !module.elements.length) {
      return;
    }
    switch (module.name) {
      case MODULES.ADVERTISEMENTS:
        return <Advertisement key={module.name} {...advertisementProps()} />;
      case MODULES.BANNER:
        if (showAiConversationBanner && homeLocale.aiConversationBanner) {
          return <AiConversationBanner key={module.name} locale={homeLocale.aiConversationBanner} aichatLocale={aichatLocale} />;
        }
        return <Banner key={module.name} />;
      case MODULES.HIGHLIGHT:
        return <Highlight key={module.name} {...highLightProps()} />;
      case MODULES.HIRED_PRODUCTS:
        return <ContractedProducts key={module.name} {...hiredProductsProps()} />;
      case MODULES.RESOURCES:
        return <Resources key={module.name} {...resourcesProps()} />;
      default:
        return null;
    }
  };

  const provisionFreePlan = useCallback(() => {
    if (!activeStudio) {
      provisioningFreePlan();
    }
  }, [provisioningFreePlan, activeStudio]);

  const modalFeatureProProps = useCallback(() => {
    const productsData = studioProducts && studioProducts.data;

    const hostingsWithoutStudios = activeHostings && activeHostings.filter(product => !HgProducts.STUDIO_ALL.includes(product.package_id));
    const hasHostingsOrDomains = (hostingsWithoutStudios && hostingsWithoutStudios.length > 0) || (activeDomains && activeDomains.length > 0);

    const purchaseTrialWithoutCc = () => {
      const onSuccessCallback = () => {
        setDisplayStudioTrialModal(false);
        history.push(routesLocale.studio, { redirectFrom: routesLocale.fastcheckout });
      };

      provisioningTrailWithoutCreditCard(onSuccessCallback);
    };

    return {
      setModalFeatureProToShow: setDisplayStudioTrialModal,
      modalId: ID_PRO_FEATURES.STUDIO_PRO_OFFER,
      trialAvailable: !!hasHostingsOrDomains,
      hasDiscount: productsData && productsData.has_discount,
      productTrial: productsData && productsData.studio_trial,
      productPro: productsData && productsData.studio_pro,
      discount: productsData && productsData.studio_discount,
      accountProduct: studioAccountProduct,
      provisionFreePlan,
      purchaseTrialWithoutCc,
      loading: provisionFree.loading || studioAccountLoading,
    };
  }, [
    studioProducts,
    activeDomains,
    activeHostings,
    routesLocale,
    history,
    studioAccountProduct,
    provisionFreePlan,
    provisioningTrailWithoutCreditCard,
    provisionFree.loading,
    studioAccountLoading,
  ]);

  if (showLoadingError) {
    return <LoadingError getHomeRequest={getHomeRequest} />;
  }

  if (homeContent && !homeContent.length) {
    return (
      <Styles.Container>
        <GreetingBanner loading />
        <Highlight loading />
        <ContractedProducts loading size={70} />
        <Advertisement loading size={30} />
        <Resources loading />
      </Styles.Container>
    );
  }

  return (
    <Styles.Container>
      {!notShowBannerPopover && <Popover steps={highlightSteps} guideRef={bannerGuideRef} storageKey={popoverStorageKey} />}
      {displayStudioTrialModal && <ModalFeaturePro {...modalFeatureProProps()} />}
      {homeContent && homeContent.map(renderModule)}
    </Styles.Container>
  );
};

export default withErrorBoundary(HomeHandler, 'HomeHandler');
